import { useEffect, useState } from 'react';
import { Button, ButtonVariant, DatePicker, DuplicatedInvoiceChip, Input, TrashIcon } from '@barkibu/noma-commons';
import Invoice from '../../../../../domain/entities/Invoice';
import { useModuleTranslation } from '../../../../../utils/useModuleTranslation';
import ExternalReviewTag from './external-review-tag/ExternalReviewTag';
import './invoice-header.scss';

export interface InvoiceHeaderProps {
    readOnly: boolean;
    invoice: Invoice;
    title?: string;
    onUpdateDate: (key: string, date: Date) => void;
    onUpdateNumber: (key: string, number: string) => void;
    onUpdateTaxIdNumber: (key: string, taxIdNumber: string) => void;
    claimRequestKey?: string;
    onDeleteInvoice?: () => void;
}

const InvoiceHeader = ({
    readOnly,
    invoice,
    title,
    onUpdateDate,
    onUpdateNumber,
    onUpdateTaxIdNumber,
    claimRequestKey,
    onDeleteInvoice,
}: InvoiceHeaderProps) => {
    const { t } = useModuleTranslation();
    const [dateSelected, setDateSelected] = useState<Date | null | undefined>(invoice.date);
    const [numberSelected, setNumberSelected] = useState(invoice.number);
    const [taxIdNumberSelected, setTaxIdNumberSelected] = useState(invoice.taxIdNumber);

    useEffect(() => {
        setDateSelected(invoice.date);
        setNumberSelected(invoice.number);
        setTaxIdNumberSelected(invoice.taxIdNumber);
    }, [invoice]);

    const handleDateChange = (date: Date | null) => {
        setDateSelected(date);
        if (date != null) {
            onUpdateDate(invoice.key!, date);
        }
    };

    const handleNumberChange = (number: string) => {
        setNumberSelected(number);
    };

    const handleNumberTouched = () => {
        if (numberSelected != null) {
            onUpdateNumber(invoice.key!, numberSelected);
        }
    };

    const handleTaxIdNumberChange = (taxIdNumber: string) => {
        setTaxIdNumberSelected(taxIdNumber);
    };

    const handleTaxIdNumberTouched = () => {
        if (taxIdNumberSelected != null) {
            onUpdateTaxIdNumber(invoice.key!, taxIdNumberSelected);
        }
    };

    return (
        <div className="invoice-header">
            <div className="invoice-header__meta">
                <div className="invoice-header__title-group">
                    <div className="invoice-header__title-row">
                        {title != null && <h3 className={'title'}>{title}</h3>}
                        {onDeleteInvoice != null && (
                            <Button
                                variantName={ButtonVariant.TERTIARY_DESTROY}
                                text={t('invoice.delete')}
                                onClick={onDeleteInvoice}
                                accessibilityText={t('invoice.delete')}
                            />
                        )}
                    </div>
                    <div className="invoice-header__tags">
                        <DuplicatedInvoiceChip
                            taxIdNumber={taxIdNumberSelected}
                            number={numberSelected}
                            date={dateSelected}
                            claimRequestKey={claimRequestKey}
                        />
                        <ExternalReviewTag externalReview={invoice.externalReview} />
                    </div>
                </div>
            </div>
            <div className="invoice-header__fields">
                <DatePicker
                    value={dateSelected}
                    label={t('invoice.date') || undefined}
                    onChange={(date) => handleDateChange(date)}
                    disabled={readOnly}
                />
                <Input
                    name="number"
                    value={numberSelected ?? ''}
                    onChange={handleNumberChange}
                    setTouched={handleNumberTouched}
                    label={t('invoice.number')}
                    disabled={readOnly}
                ></Input>
                <Input
                    name="taxIdNumber"
                    value={taxIdNumberSelected ?? ''}
                    onChange={handleTaxIdNumberChange}
                    setTouched={handleTaxIdNumberTouched}
                    label={t('invoice.cif')}
                    disabled={readOnly}
                ></Input>
            </div>
        </div>
    );
};

export default InvoiceHeader;
